import { useState } from 'react';
import { Box, Typography, Button, Snackbar, Alert } from '@mui/material';
import { Add, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import documentsService from '../services/documents.service';
import { Document } from '../types';
import { DocumentsTable, DocumentsToolbar } from '../components/documents/DocumentsTable';
import { DocumentsForm } from '../components/documents/DocumentsForm';

export default function DocumentsPage() {
  const [page, setPage] = useState(1);
  const [limit] = useState(20);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<Document | null>(null);
  const [notification, setNotification] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const queryClient = useQueryClient();

  const { data: documentsData, isLoading } = useQuery({
    queryKey: ['documents', { page, limit, search, categoryFilter }],
    queryFn: () => documentsService.getList({ page, limit, search, category: categoryFilter || undefined }),
  });

  const createMutation = useMutation({
    mutationFn: (data: Omit<Document, 'id' | 'createdAt' | 'updatedAt'>) => documentsService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setIsFormOpen(false);
      setEditingItem(null);
      setNotification({ message: 'Документ добавлен', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при создании', type: 'error' }),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Document> }) => documentsService.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setIsFormOpen(false);
      setEditingItem(null);
      setNotification({ message: 'Документ обновлен', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при обновлении', type: 'error' }),
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => documentsService.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['documents'] });
      setNotification({ message: 'Документ удален', type: 'success' });
    },
    onError: () => setNotification({ message: 'Ошибка при удалении', type: 'error' }),
  });

  const filteredItems = documentsData?.data || [];
  const total = documentsData?.pagination?.total || 0;

  const handleCreate = () => { setEditingItem(null); setIsFormOpen(true); };
  const handleEdit = (item: Document) => { setEditingItem(item); setIsFormOpen(true); };
  const handleSubmit = async (data: any) => {
    // Transform files array for backend
    const submitData = {
      title: data.title,
      category: data.category,
      description: data.description,
      fileUrl: data.externalLink || (data.files?.[0]?.url || ''),
      files: data.files || [],
    };
    if (editingItem) await updateMutation.mutateAsync({ id: editingItem.id, data: submitData });
    else await createMutation.mutateAsync(submitData);
  };
  const handleDelete = (id: number) => { if (window.confirm('Удалить документ?')) deleteMutation.mutate(id); };
  const handleCloseNotification = () => setNotification(null);

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ fontFamily: 'Noto Serif' }}>Документы</Typography>
        <Button variant="contained" startIcon={<Add />} onClick={handleCreate}>Добавить документ</Button>
      </Box>
      <DocumentsToolbar 
        search={search} 
        onSearchChange={(value) => { setSearch(value); setPage(1); }} 
        categoryFilter={categoryFilter} 
        onCategoryFilterChange={(value) => { setCategoryFilter(value); setPage(1); }} 
      />
      <DocumentsTable 
        items={filteredItems} 
        loading={isLoading} 
        total={total} 
        page={page} 
        limit={limit} 
        onPageChange={setPage} 
        onEdit={handleEdit} 
        onDelete={handleDelete} 
      />
      <DocumentsForm 
        open={isFormOpen} 
        onClose={() => setIsFormOpen(false)} 
        onSubmit={handleSubmit} 
        initialData={editingItem as any} 
        loading={createMutation.isPending || updateMutation.isPending} 
        error={createMutation.error?.message || updateMutation.error?.message} 
      />
      <Snackbar open={!!notification} autoHideDuration={3000} onClose={handleCloseNotification} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={handleCloseNotification} severity={notification?.type === 'success' ? 'success' : 'error'} sx={{ width: '100%' }} icon={notification?.type === 'success' ? <CheckCircle /> : <ErrorIcon />}>{notification?.message}</Alert>
      </Snackbar>
    </Box>
  );
}